import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ProductSelectionService } from '../../../Shared/product-selection/product-selection.service';

export interface Product {
  name: string;
  label: string;
  subtitle: string;
  iconKey: string;
  image: string;
  url: string;
}

export interface ProductGroup {
  heading: string;
  color: string;
  products: Product[];
}

function product(section: string, label: string, subtitle: string, iconKey: string): Product {
  return { name: `${section} - ${label}`, label, subtitle, iconKey, image: '', url: '#' };
}

@Component({
  selector: 'app-product-app',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './product-app.html',
  styleUrl: './product-app.css',
})
export class ProductApp {
  private router = inject(Router);
  private productSelection = inject(ProductSelectionService);

  // Restore a prior selection so navigating Back from /register (via "Change apps selection")
  // doesn't lose what was already picked.
  selectedProducts: Product[] = [...this.productSelection.selectedApps];
  readonly maxSelectable = 6;

  readonly groups: ProductGroup[] = [
    {
      heading: 'Sales',
      color: '#ef4444',
      products: [
        product('Sales', 'Lead Management', 'Capture and Qualify New Leads', 'leads'),
        product('Sales', 'Quotations', 'Send Quotes Customers Can Approve', 'quotations'),
        product('Sales', 'Sales Orders', 'Track Orders Through Fulfilment', 'sales-orders'),
        product('Sales', 'Invoicing', 'Bill Customers Without the Back-and-Forth', 'invoicing'),
        product('Sales', 'Report & Analytics', 'See What is Actually Selling', 'reports'),
      ],
    },
    {
      heading: 'Human Resource',
      color: '#14b8a6',
      products: [
        product('Human Resource', 'Recruitment', 'Efficient Employee Recruitment', 'recruitment'),
        product('Human Resource', 'Employee', 'Employee Management Simplified', 'employee'),
        product('Human Resource', 'Payroll', 'Run Payroll Without the Spreadsheets', 'payroll'),
        product('Human Resource', 'Tasks', 'Assign and Track Team Tasks', 'tasks'),
        product('Human Resource', 'Report & Analytics', 'Workforce Insights at a Glance', 'reports'),
      ],
    },
    {
      heading: 'Inventory',
      color: '#3b82f6',
      products: [
        product('Inventory', 'Stock Management', 'Know Exactly What is in Stock', 'stock'),
        product('Inventory', 'Warehouses', 'Manage Multiple Storage Locations', 'warehouse'),
        product('Inventory', 'Stock Transfers', 'Move Stock Between Locations', 'transfer'),
        product('Inventory', 'Barcode Scanning', 'Scan Items In and Out Fast', 'barcode'),
        product('Inventory', 'Report & Analytics', 'Spot Low Stock Before it Runs Out', 'reports'),
      ],
    },
    {
      heading: 'Procurement',
      color: '#ef4444',
      products: [
        product('Procurement', 'Purchase Orders', 'Raise and Track Purchase Orders', 'purchase-orders'),
        product('Procurement', 'Vendor Management', 'Keep Every Supplier in One Place', 'vendor'),
        product('Procurement', 'Request for Quotation', 'Compare Supplier Quotes Fast', 'rfq'),
        product('Procurement', 'Purchase Approvals', 'Route Purchases for Sign-off', 'approvals'),
        product('Procurement', 'Report & Analytics', 'Track Spend Across Suppliers', 'reports'),
      ],
    },
    {
      heading: 'Manufacturing',
      color: '#3b82f6',
      products: [
        product('Manufacturing', 'Bill of Materials', 'Define What Each Product Needs', 'bom'),
        product('Manufacturing', 'Work Orders', 'Schedule and Track Production Jobs', 'work-orders'),
        product('Manufacturing', 'Production Planning', 'Plan Runs Around Real Capacity', 'production'),
        product('Manufacturing', 'Quality Control', 'Catch Defects Before They Ship', 'quality'),
        product('Manufacturing', 'Report & Analytics', 'Monitor Output and Wastage', 'reports'),
      ],
    },
    {
      heading: 'CRM',
      color: '#14b8a6',
      products: [
        product('CRM', 'Contact Management', 'One Record for Every Customer', 'contacts'),
        product('CRM', 'Deals Pipeline', 'See Every Deal and its Stage', 'pipeline'),
        product('CRM', 'Customer Support', 'Resolve Tickets Without the Chaos', 'support'),
        product('CRM', 'Campaigns', 'Run Outreach Straight From CRM', 'campaigns'),
        product('CRM', 'Report & Analytics', 'Track Relationships That Convert', 'reports'),
      ],
    },
  ];

  toggleProduct(product: Product): void {
    const idx = this.selectedProducts.findIndex(p => p.name === product.name);
    if (idx === -1) {
      if (this.selectedProducts.length >= this.maxSelectable) {
        return;
      }
      this.selectedProducts.push(product);
    } else {
      this.selectedProducts.splice(idx, 1);
    }
  }

  isLimitReached(): boolean {
    return this.selectedProducts.length >= this.maxSelectable;
  }

  isSelected(product: Product): boolean {
    return this.selectedProducts.some(p => p.name === product.name);
  }

  onContinue(): void {
    this.productSelection.selectedApps = this.selectedProducts;
    this.router.navigate(['/register']);
  }
}
